import { useInView } from 'react-intersection-observer';
import { toQueryString } from '@croquiscom/web2app';
import { callDeepLink } from '@croquiscom-pvt/web2app';

import { gotoLogin } from '@common/utils';
import {
  PdpCatalogFulfillmentType,
  PdpCouponAvailableStatus,
  Router,
  useCatalogProductIdParams,
  useIsAuthenticated,
  usePdpBaseInfo,
  usePdpConfig,
} from '@domains/pdp-common';

import { useCouponUBL } from '../log/useCouponUBL';
import { usePromotionUBL } from '../log/usePromotionUBL';

export const useCouponService = () => {
  const { impressionCouponBannerUBL, clickCouponBannerUBL, couponClickUBL } = useCouponUBL();
  const promotionLog = usePromotionUBL();
  const params = useCatalogProductIdParams();
  const {
    product: {
      id,
      browsing_type,
      fulfillment_type,
      coupon_available_status,
      trait_list,
      promotion_info,
      product_price: {
        display_final_price,
        coupon_button_color: { normal: coupon_button_of_atf },
      },
    },
    shop,
  } = usePdpBaseInfo(params);
  const isSample = trait_list?.some(({ type }) => type === 'SAMPLE');

  const isPromotion = Boolean(promotion_info?.float_banner_info);
  const is_coupon_available = coupon_available_status === PdpCouponAvailableStatus.COUPON_AVAILABLE;

  const impressionCouponBannerRef = useInView({
    onChange: (inView) => {
      if (!inView) {
        return;
      }
      if (isPromotion) {
        const finalPrice = display_final_price?.final_price.price ?? 0;
        const finalPriceAdditional = display_final_price?.final_price_additional?.price ?? 0;
        promotionLog.impressionPromotionCouponUBL(
          (finalPriceAdditional ?? finalPrice).toString(),
          id,
          browsing_type,
          is_coupon_available,
        );
      } else {
        impressionCouponBannerUBL(id, is_coupon_available);
      }
    },
    triggerOnce: true,
  });

  const { isZigzagApp } = usePdpConfig();
  const { isLoggedIn } = useIsAuthenticated();

  const createCouponDownloadUrl = () => {
    return `/coupon/box/${shop.id}?${toQueryString({
      product_id_list: [id],
      is_zonly: fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN,
      from: 'pdp',
    })}`;
  };

  const redirectToLogin = () => {
    if (isZigzagApp) {
      callDeepLink('/login')();
      return;
    }

    gotoLogin({ redirect_url: createCouponDownloadUrl() });
  };

  const handleClickDownload = () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }

    if (isPromotion) {
      promotionLog.clickPromotionCouponUBL(id, browsing_type, is_coupon_available);
    } else {
      couponClickUBL(id);
      clickCouponBannerUBL(id, is_coupon_available);
    }

    Router.open(createCouponDownloadUrl());
  };

  return {
    isHide:
      coupon_available_status === PdpCouponAvailableStatus.COUPON_UNAVAILABLE ||
      coupon_available_status === PdpCouponAvailableStatus.COUPON_NOT_APPLICABLE ||
      isSample,
    isCouponAvailable: is_coupon_available,

    buttonColor: coupon_button_of_atf,

    impressionCouponBannerRef: impressionCouponBannerRef.ref,
    handleClickDownload,
  };
};
